import React, { FC, useEffect, useState } from 'react'
import { createUseStyles } from 'react-jss'
import { useAtom } from 'jotai'
import { Drawer } from '../../ui/Drawer/Drawer'
import { Button } from '../../ui/Button/Button'
import { Settings } from '../../../assets/icons'
import {
    FilterPosition,
    FilterTRFor,
    FilterTypeOfRepresentative,
    FilterUpdateStatus,
    FilterWorkGroup,
    FilterWorkplace,
    IFilter,
    initialSelectedFilters,
} from '../../../models/Filter'
import {
    updateGroups,
    updateTypeOfRepresentatives,
    updatePositions,
    updateWorkplaces,
    updateTRFor,
    updateUpdateStatus,
} from '../../../utils/filterhelper'
import { Accordion } from '../../ui/Accordion/Accordion'
import { Checkboxes } from './Checkboxes'
import { filterAtom, filterSelected } from '../../../store/filter/filterAtom'
import { Overlay } from '../../ui/Sidebar'
import { Typography } from '../../ui/Typography/Typography'
import { L, L_HTML } from '../../providers/Lokalise'
import Checkbox from '../../ui/FormElememts/Checkbox'
import { colors } from '../../../theme/palette'
import {
    contentWrapper,
    formControl,
    accordionWrapper,
    filterBottom,
} from '../../../theme/reuseables'
import breakpoints from '../../../theme/breakpoints'
import { useWindowResize } from '../../../hooks/useWindowSize'

const useStyles = createUseStyles({
    filtersContainer: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        alignItems: 'flex-end',
        [`@media (min-width: ${breakpoints.Desktop}px)`]: {
            '&': {
                flexDirection: 'row',
                gap: '0',
                alignItems: 'flex-start',
                width: 'auto',
            },
        },
    },
    contentWrapper: {
        ...contentWrapper,
    },
    formControl: {
        ...formControl,
    },
    accordionWrapper: {
        ...accordionWrapper,
    },
    filterBottom: {
        ...filterBottom,
    },
})

export const Filter: FC = () => {
    const [selectedItems, setSelectedItems] = useState<IFilter>({
        ...initialSelectedFilters,
    })
    const classes = useStyles()
    const [filter] = useAtom(filterAtom)
    const [selected, setSelected] = useAtom(filterSelected)
    const [isOpen, setIsOpen] = useState(false)
    const windowSize = useWindowResize()
    const onSubmit = (): void => {
        setIsOpen(false)
        setSelected(selectedItems)
    }

    useEffect(() => {
        setSelectedItems(selected)
    }, [selected])

    const onUpdateWorkplaces = (workPlace: FilterWorkplace): void => {
        const items = updateWorkplaces(workPlace, selectedItems)
        setSelectedItems(items)
    }

    const onUpdateGroups = (workGroup: FilterWorkGroup): void => {
        const items = updateGroups(workGroup, selectedItems)
        setSelectedItems(items)
    }

    const onUpdatePositions = (position: FilterPosition): void => {
        const items = updatePositions(position, selectedItems)
        setSelectedItems(items)
    }

    const onUpdateTypeOfRepresentatives = (
        pot: FilterTypeOfRepresentative
    ): void => {
        const items = updateTypeOfRepresentatives(pot, selectedItems)
        setSelectedItems(items)
    }

    const onUpdateTRFor = (trFor: FilterTRFor): void => {
        const items = updateTRFor(trFor, selectedItems)
        setSelectedItems(items)
    }

    const onUpdateStatus = (status: FilterUpdateStatus): void => {
        const items = updateUpdateStatus(status, selectedItems)
        setSelectedItems(items)
    }

    const resetFilter = (): void => {
        setSelectedItems(initialSelectedFilters)
        setSelected(initialSelectedFilters)
    }

    return (
        <div className={classes.filtersContainer}>
            {windowSize.width > breakpoints.Desktop && (
                <Button
                    type="primary"
                    size="large"
                    icon={<Settings fill="#fff" />}
                    onClick={() => setIsOpen(true)}
                    style={{ paddingLeft: '10px' }}
                >
                    {L('MINEMEDLEMMER_FILTER_KNAP')}
                </Button>
            )}
            {windowSize.width < breakpoints.Desktop && (
                <Button
                    type="primary"
                    size="large"
                    icon={<Settings fill="#fff" />}
                    onClick={() => setIsOpen(true)}
                    style={{ paddingLeft: '10px' }}
                >
                    Filtrer
                </Button>
            )}
            <Overlay isShowing={isOpen} hide={() => setIsOpen(false)}>
                <Typography
                    tag="h2"
                    size="XL"
                    align="center"
                    weight="bold"
                    style={{ margin: '20px 0 10px' }}
                >
                    {L_HTML('MINEMEDLEMMER_FILTER_TITEL_KNAP')}
                </Typography>
                <div className={classes.contentWrapper}>
                    <div className={classes.accordionWrapper}>
                        <Accordion
                            title={L('MINEMEDLEMMER_FILTER_ARBEJDSTED_LABEL')}
                        >
                            <Checkboxes
                                items={filter.workplaces}
                                handleChange={onUpdateWorkplaces}
                                isSelected={(
                                    workPlace: FilterWorkplace
                                ): boolean =>
                                    !!selectedItems.workplaces.find(
                                        (w) => w.id === workPlace.id
                                    )
                                }
                            />
                        </Accordion>
                    </div>
                    <div className={classes.accordionWrapper}>
                        <Accordion
                            title={L('MINEMEDLEMMER_FILTER_FAGGRUPPE_LABEL')}
                        >
                            <Checkboxes
                                items={filter.groups}
                                handleChange={onUpdateGroups}
                                isSelected={(group: FilterWorkGroup) =>
                                    !!selectedItems.groups.find(
                                        (w) => w.id === group.id
                                    )
                                }
                            />
                        </Accordion>
                    </div>
                    <div className={classes.accordionWrapper}>
                        <Accordion
                            title={L('MINEMEDLEMMER_FILTER_STILLING_LABEL')}
                        >
                            <Checkboxes
                                items={filter.positions}
                                handleChange={onUpdatePositions}
                                isSelected={(position: FilterPosition) =>
                                    !!selectedItems.positions.find(
                                        (w) => w.id === position?.id
                                    )
                                }
                            />
                        </Accordion>
                    </div>
                    <div className={classes.accordionWrapper}>
                        <Accordion
                            title={L('MINEMEDLEMMER_FILTER_TILLIDSHVERV_LABEL')}
                        >
                            <Checkboxes
                                items={filter.typeOfRepresentative}
                                handleChange={onUpdateTypeOfRepresentatives}
                                isSelected={(
                                    trust: FilterTypeOfRepresentative
                                ) =>
                                    !!selectedItems.typeOfRepresentative.find(
                                        (t) => t.name === trust.name
                                    )
                                }
                            />
                        </Accordion>
                    </div>

                    <div className={classes.accordionWrapper}>
                        <Accordion
                            title={L('MINEMEDLEMMER_FILTER_TRFOR_LABEL')}
                        >
                            <>
                                {filter.representing.map(
                                    (trfor: FilterTRFor) => {
                                        return (
                                            <div
                                                key={trfor.displayName}
                                                className={classes.formControl}
                                            >
                                                <Checkbox
                                                    onChange={() => {
                                                        onUpdateTRFor(trfor)
                                                    }}
                                                    label={trfor.displayName}
                                                    isChecked={
                                                        !!selectedItems.representing.find(
                                                            (t) =>
                                                                t.name ===
                                                                trfor.name
                                                        )
                                                    }
                                                />
                                            </div>
                                        )
                                    }
                                )}
                            </>
                        </Accordion>
                    </div>

                    <div className={classes.accordionWrapper}>
                        <Accordion
                            title={L('MINEMEDLEMMER_FILTER_STATUS_LABEL')}
                        >
                            {filter.updateStatus.map((status) => {
                                return (
                                    <div
                                        key={status.id}
                                        className={classes.formControl}
                                    >
                                        <Checkbox
                                            onChange={() => {
                                                onUpdateStatus(status)
                                            }}
                                            label={status.name}
                                            isChecked={
                                                !!selectedItems.updateStatus.find(
                                                    (t) => t.id === status.id
                                                )
                                            }
                                        />
                                    </div>
                                )
                            })}
                        </Accordion>
                    </div>

                    <div className={classes.filterBottom}>
                        <Button
                            onClick={resetFilter}
                            type="secondary"
                            size="large"
                        >
                            {L('MINEMEDLEMMER_FILTER_RYD-FILTRE_KNAP')}
                        </Button>
                        <Button onClick={onSubmit} size="large">
                            {L('MINEMEDLEMMER_FILTER_ANVEND_KNAP')}
                        </Button>
                    </div>
                </div>
            </Overlay>
        </div>
    )
}
